import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'src/config';
import { setTranslationKeys, setTranslations } from 'src/features/translations/slices/translationsSlice';
import { getAuthAccessToken } from 'src/configuration/tokenHandling/authSlice';
import { createErrorResponse, createSuccessResponse } from 'src/services/responseCreator';

const TAG_TYPE_TRANSLATIONS = 'Translations';
const POST = 'POST';
const url = config.backend.translation.TRANSLATION_SERVICE;
const translationPath = config.backend.translation.TRANSLATION_PATH;
const versionsPath = config.backend.translation.VERSIONS_PATH;
const translationKeysPath = config.backend.translation.TRANSLATION_KEYS_PATH;
const baseQuery = fetchBaseQuery({
    baseUrl: url,
    prepareHeaders: (headers, { endpoint, getState, extra, type }) => {
        // Use type or extra to determine method if available, otherwise use endpoint name
        // If you have access to the request method, use it:
        // For GET requests
        if (type === 'query') {
            headers.set('Accept', 'application/json');
        }
        // For POST/Mutation requests
        if (type === 'mutation') {
            headers.set('Accept', 'application/json');
        }
        // Add Authorization if needed
        const token = getAuthAccessToken(getState());
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});
export const translationsApi = createApi({
    reducerPath: 'translationsApi',
    baseQuery,
    tagTypes: [TAG_TYPE_TRANSLATIONS],
    endpoints: (builder) => ({
        getTranslations: builder.query({
            query: () => translationPath,
            async onCacheEntryAdded(arg, { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
                try {
                    const { data } = await cacheDataLoaded;
                    dispatch(setTranslations(data));
                } catch (error) {
                    console.error('onCacheEntryAdded error: ', error);
                }
                await cacheEntryRemoved;
            },
            providesTags: [TAG_TYPE_TRANSLATIONS],
            keepUnusedDataFor: 0, // override the default of 60 seconds
            transformResponse: (baseQueryReturnValue, meta, arg) => {
                const translationsByCountries = baseQueryReturnValue.translations;
                const sortCountrySubobjects = (data, f) => {
                    const sortedData = {};
                    Object.keys(data)
                        .sort(f())
                        .forEach((countryCode) => {
                            const categories = data[countryCode];
                            const sortedCategories = {};
                            Object.keys(categories)
                                .sort(f())
                                .forEach((category) => {
                                    const translations = categories[category];
                                    const sortedKeys = Object.keys(translations).sort(f());
                                    const sortedTranslations = {};
                                    sortedKeys.forEach((key) => {
                                        sortedTranslations[key] = translations[key];
                                    });
                                    sortedCategories[category] = sortedTranslations;
                                });
                            sortedData[countryCode] = sortedCategories;
                        });
                    return sortedData;
                };
                const sortedTrans = sortCountrySubobjects(
                    translationsByCountries,
                    () => (a, b) => a.toLowerCase().localeCompare(b.toLowerCase())
                );
                return {
                    ...baseQueryReturnValue,
                    translations: sortedTrans,
                };
            },
        }),
        postTranslations: builder.mutation({
            query: (body) => ({
                url: translationPath,
                method: POST,
                body,
            }),
            transformResponse: (response, meta, arg) => {
                return createSuccessResponse(response, meta, arg);
            },
            transformErrorResponse: (response, meta, arg) => {
                return createErrorResponse(response, meta, arg, 'Error on uploading translations');
            },
            providesTags: [TAG_TYPE_TRANSLATIONS],
        }),
        getVersions: builder.query({
            query: () => versionsPath,
            // async onCacheEntryAdded(arg, { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
            //     try {
            //         const { data } = await cacheDataLoaded;
            //         dispatch(setVersions(data));
            //     } catch (error) {
            //         console.error('onCacheEntryAdded error: ', error);
            //     }
            //     await cacheEntryRemoved;
            // },
            providesTags: [TAG_TYPE_TRANSLATIONS],
            keepUnusedDataFor: 0, // override the default of 60 seconds
        }),
        getTranslationKeys: builder.query({
            // query: () => translationKeysPath,
            query: () => ({
                url: translationKeysPath,
                method: 'GET',
                // responseHandler: (response) => response.blob(),
            }),

            async onCacheEntryAdded(arg, { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
                try {
                    const { data } = await cacheDataLoaded;
                    dispatch(setTranslationKeys(data));
                } catch (error) {
                    console.error('onCacheEntryAdded error: ', error);
                }
                await cacheEntryRemoved;
            },
            providesTags: [TAG_TYPE_TRANSLATIONS],
            keepUnusedDataFor: 0, // override the default of 60 seconds
        }),
    }),
});

export const { useGetTranslationsQuery, usePostTranslationsMutation, useGetVersionsQuery, useGetTranslationKeysQuery } =
    translationsApi;
